import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../shared/ui/Modal';
import { sendAuthUserOTP, authVerifyOtp, authChangePassword } from '../../auth/api';

const STEPS = {
  1: { title: "Change password", subtitle: "Step 1 of 3 · Request a verification code" },
  2: { title: "Enter verification code", subtitle: "Step 2 of 3 · Enter the code we emailed you" },
  3: { title: "Set a new password", subtitle: "Step 3 of 3 · Choose your new password" },
};

const ChangePasswordModal = ({ isOpen, onClose, email }) => {
  // Start at Step 1: Request OTP
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [resetLoading, setResetLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false); // Indicates OTP is being sent

  // Start or reset countdown when in OTP step (Step 2)
  useEffect(() => {
    let timerId;
    if (step === 2) {
      setResendTimer(60);
      timerId = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(timerId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [step]);

  // Function to send OTP when requested by user (Step 1 or Resend)
  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!email) {
      toast.error("Email is required.");
      return;
    }
    setSendingOtp(true);
    try {
      await sendAuthUserOTP();
      // After sending OTP, move to OTP verification step
      setStep(2);
    } catch (error) {
      console.error("Error sending OTP:", error.response?.data || error.message);
      toast.error("Error sending OTP. Please try again.");
    }
    setSendingOtp(false);
  };

  // Allow only numeric input for OTP and auto-focus next input
  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (value && isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && e.target.nextSibling) {
      e.target.nextSibling.focus();
    }
  };

  const isOtpComplete = otp.every(val => val !== '');

  // Handle OTP verification: combine OTP digits, verify, and store token
  const handleOtpVerify = async (e) => {
    e?.preventDefault();
    if (!isOtpComplete) {
      toast.info("Please enter the complete OTP.");
      return;
    }
    const otpValue = otp.join('');
    setOtpVerifying(true);
    try {
      const response = await authVerifyOtp(otpValue);
      toast.success("OTP Verification successful.");
      setOtpToken(response.token);
      // Move to change password step
      setStep(3);
    } catch (error) {
      console.error("OTP Verification failed:", error.response?.data || error.message);
      let message = error.response?.data || "An error occurred.";
      toast.error(message);
    }
    setOtpVerifying(false);
  };

  // Handle Resend OTP functionality
  const handleResendOtp = async () => {
    try {
      await sendAuthUserOTP();
      setResendTimer(60); // reset timer
      toast.info("OTP Resent successfully.");
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Error resending OTP. Please try again.");
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    if (!password || password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setResetLoading(true);
    try {
      await authChangePassword(otpToken, password);
      toast.success("Password reset successful.");
      onClose();
      window.location.reload(); // Reload the page to reflect changes
    } catch (error) {
      console.error("Password reset failed:", error.response?.data || error.message);
      toast.error("Password reset failed. Please try again.");
    }
    setResetLoading(false);
  };

  const busy = sendingOtp || otpVerifying || resetLoading;

  const footer = (
    <>
      <button type="button" className="ui-btn ui-btn--ghost" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      {step === 1 && (
        <button type="submit" form="change-password-form" className="ui-btn ui-btn--primary" disabled={sendingOtp}>
          {sendingOtp ? "Sending OTP..." : "Send OTP"}
        </button>
      )}
      {step === 2 && (
        <button
          type="submit"
          form="change-password-form"
          className="ui-btn ui-btn--primary"
          disabled={!isOtpComplete || otpVerifying}
        >
          {otpVerifying ? "Verifying..." : "Confirm"}
        </button>
      )}
      {step === 3 && (
        <button type="submit" form="change-password-form" className="ui-btn ui-btn--primary" disabled={resetLoading}>
          {resetLoading ? "Submitting..." : "Submit"}
        </button>
      )}
    </>
  );

  const passwordToggle = (
    <button
      type="button"
      className="ui-input-wrap__action"
      onClick={() => setShowPassword((show) => !show)}
      aria-label={showPassword ? "Hide password" : "Show password"}
    >
      <i className={`ph ${showPassword ? "ph-eye-slash" : "ph-eye"}`} />
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={STEPS[step].title}
      subtitle={STEPS[step].subtitle}
      size="sm"
      dismissible={!busy}
      footer={footer}
    >
      <form
        id="change-password-form"
        className="ui-form"
        onSubmit={step === 1 ? sendOtp : step === 2 ? handleOtpVerify : handleChangePassword}
      >
        {step === 1 && (
          <p className="ui-note">
            <i className="ph ph-envelope-simple" />
            <span>
              Click the button below to request an OTP to change your password for <strong>{email}</strong>.
            </span>
          </p>
        )}

        {step === 2 && (
          <>
            <p className="ui-hint">
              We have sent a verification code to <strong>{email}</strong>. Please enter it below.
            </p>
            <div className="ui-otp">
              {otp.map((data, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={data}
                  onChange={(e) => handleOtpChange(e, index)}
                  className="ui-input ui-otp__digit"
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>
            <div className="ui-otp__resend">
              {resendTimer > 0 ? (
                <p className="ui-hint">Resend OTP in {resendTimer} seconds</p>
              ) : (
                <button type="button" className="ui-btn ui-btn--link" onClick={handleResendOtp}>
                  Resend OTP
                </button>
              )}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="ui-field">
              <label className="ui-label" htmlFor="change-password-new">New password</label>
              <div className="ui-input-wrap">
                <input
                  id="change-password-new"
                  type={showPassword ? "text" : "password"}
                  className="ui-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {passwordToggle}
              </div>
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="change-password-confirm">Confirm password</label>
              <input
                id="change-password-confirm"
                type={showPassword ? "text" : "password"}
                className="ui-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
